import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Rate, message, Select, Row, Col, Space, Card } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import ProductFeedbackServ from "../service/ProductFeedbackServ";
import ProductServ from "../service/ProductServ";
import TableImage from "./ui/TableImage";
import { getImageUrl as getNormalizedImageUrl } from "./SafeImage";

const { Option } = Select;

const AddProductFeedback = ({
  open,
  refreshFeedback,
  feedbackDetails,
  isEditing,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 🧩 Fetch product list for dropdown
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await ProductServ.index();
        setProducts(response?.data || []);
      } catch (error) {
        console.error("Failed to load products", error);
        message.error("Failed to load products");
      }
    };
    fetchProducts();
  }, []);

  // 🧩 Fill form values while editing
  useEffect(() => {
    if (isEditing && feedbackDetails) {
      const pId = feedbackDetails.product?._id || feedbackDetails.product || feedbackDetails.productId;
      form.setFieldsValue({
        productId: pId,
        userName: feedbackDetails.user ? `${feedbackDetails.user.firstName || ''} ${feedbackDetails.user.lastName || ''}`.trim() : feedbackDetails.userName,
        rating: feedbackDetails.rating,
        comment: feedbackDetails.comment,
      });
      if (pId && products.length > 0) {
        const prod = products.find(p => (p.id || p._id) === pId);
        setSelectedProduct(prod);
      }
    } else {
      form.resetFields();
      setSelectedProduct(null);
    }
  }, [feedbackDetails, isEditing, form, products]);

  // 🧩 Submit handler
  const onFinish = async (values) => {
    console.log("✅ onFinish triggered with:", values);
    setSubmitting(true);
    try {
      const payload = {
        id: feedbackDetails?.id,
        product: values.productId,
        userName: values.userName,
        rating: values.rating,
        comment: values.comment,
        feedback: values.comment, // backend expects this
        createdAt: new Date().toISOString(),
      };

      const feedbackId = feedbackDetails?.id || feedbackDetails?._id;

      if (isEditing && feedbackId) {
        await ProductFeedbackServ.update(feedbackId, payload);
        message.success("Feedback updated successfully!");
      } else {
        await ProductFeedbackServ.create(payload);
        message.success("Feedback added successfully!");
      }

      handleClose(true);
    } catch (error) {
      console.error(error);
      message.error("Something went wrong while saving feedback!");
    } finally {
      setSubmitting(false);
    }
  };

  // 🧩 Handle close
  const handleClose = (shouldRefresh) => {
    refreshFeedback(shouldRefresh);
    form.resetFields();
  };

  return (
    <Modal
      open={open}
      onCancel={() => handleClose(false)}
      width={800}
      centered={true}
      title={null}
      closable={false}
      footer={null}
      styles={{ body: { padding: 0 } }}
      bodyStyle={{ padding: 0 }}
      style={{ borderRadius: '16px', overflow: 'hidden' }}
    >
      {/* Premium Header Layout matching Image 2 */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "18px 24px",
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "42px",
            height: "42px",
            background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)"
          }}>
            <MessageOutlined style={{ fontSize: "20px", color: "#4f46e5" }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#1e293b", letterSpacing: "-0.3px" }}>
              {isEditing ? "Modify Customer Feedback" : "Configure Product Feedback"}
            </h1>
            <p style={{ margin: 0, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              Standardized Cross-Tab Validation Active
            </p>
          </div>
        </div>
        <div 
          onClick={() => handleClose(false)} 
          style={{ 
            fontSize: '16px', 
            color: '#64748b', 
            cursor: 'pointer', 
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
        >
          ✕
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Modal Scrollable Content Area */}
        <div style={{ padding: "24px", maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}>
          
          <Row gutter={24}>
            {/* Left Side: Product selection & User Name */}
            <Col span={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Product Dropdown */}
                <Form.Item
                  label={<span style={{ fontWeight: 800, color: '#475569', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>* Product selection</span>}
                  name="productId"
                  rules={[{ required: true, message: "Please select a product" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select 
                    placeholder="Select product" 
                    size="middle" 
                    style={{ width: '100%', borderRadius: '8px' }}
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => {
                      const prod = products.find(p => (p.id || p._id) === val);
                      setSelectedProduct(prod);
                    }}
                  >
                    {products.map((p) => (
                      <Option key={p.id || p._id} value={p.id || p._id}>
                        <Space>
                          <TableImage src={getNormalizedImageUrl(p.imageUrl)} size={24} radius={4} />
                          <span style={{ fontWeight: 600 }}>{p.name}</span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedProduct && (
                  <Card 
                    size="small" 
                    styles={{ body: { padding: '6px 10px' } }}
                    bodyStyle={{ padding: '6px 10px' }}
                    style={{ 
                      background: '#ffffff', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.01)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        src={getNormalizedImageUrl(selectedProduct.imageUrl)}
                        alt="Selected"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          objectFit: 'cover',
                          border: '1px solid #e2e8f0',
                          background: '#fff'
                        }}
                      />
                      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>
                            {selectedProduct.name}
                          </div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>
                            Price: ₹{selectedProduct.price} • Stock: {selectedProduct.stock}
                          </div>
                        </div>
                        {selectedProduct.shopName && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: '#FF8800', background: '#fff7ed', padding: '1px 6px', borderRadius: '4px' }}>
                            {selectedProduct.shopName}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Username */}
                <Form.Item
                  label={<span style={{ fontWeight: 800, color: '#475569', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>* User Name</span>}
                  name="userName"
                  rules={[{ required: true, message: "Please enter your name" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Enter your name" disabled={isEditing} size="middle" style={{ borderRadius: '8px', fontWeight: 600 }} />
                </Form.Item>

              </div>
            </Col>

            {/* Right Side: Rating & Comment */}
            <Col span={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Rating */}
                <Form.Item
                  label={<span style={{ fontWeight: 800, color: '#475569', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>* Rating</span>}
                  name="rating"
                  rules={[{ required: true, message: "Please select a rating" }]}
                  style={{ marginBottom: 0 }}
                >
                  <div style={{ background: '#f8fafc', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'inline-flex', alignItems: 'center' }}>
                    <Rate style={{ fontSize: '18px' }} />
                  </div>
                </Form.Item>

                {/* Comment */}
                <Form.Item
                  label={<span style={{ fontWeight: 800, color: '#475569', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>* Comment</span>}
                  name="comment"
                  rules={[{ required: true, message: "Please enter a comment" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea rows={4} placeholder="Write your feedback" style={{ borderRadius: '8px', fontSize: '13px' }} />
                </Form.Item>

              </div>
            </Col>
          </Row>

        </div>

        {/* Edge-to-Edge Custom Footer */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px"
        }}>
          {/* Left: Sync Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#10b981', background: '#ecfdf5', padding: '6px 12px', borderRadius: '20px', border: '1px solid #a7f3d0' }}>
            <span style={{ fontSize: '12px' }}>🛡</span> SECURE SYNC
          </div>

          {/* Right: Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              onClick={() => handleClose(false)}
              disabled={submitting}
              style={{
                height: '36px',
                borderRadius: '18px',
                fontWeight: 700,
                fontSize: '12px',
                color: '#64748b',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                padding: '0 20px',
                cursor: 'pointer',
                letterSpacing: '0.5px'
              }}
            >
              CANCEL
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={() => form.submit()}
              style={{
                height: '36px',
                borderRadius: '18px',
                fontWeight: 700,
                fontSize: '12px',
                background: 'linear-gradient(135deg, #FF5E00 0%, #FF8800 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(255, 94, 0, 0.2)',
                padding: '0 24px',
                color: '#fff',
                cursor: 'pointer',
                letterSpacing: '0.5px'
              }}
            >
              {isEditing ? "UPDATE FEEDBACK" : "SUBMIT FEEDBACK"}
            </Button>
          </div>
        </div>

      </Form>
    </Modal>
  );
};

export default AddProductFeedback;
